/**
 * CMR Read Service - Fetches patient CMR (Continuous Medical Record) data for ChartMind
 *
 * Owner reads: direct Firestore client get (unchanged).
 * Non-owner reads: readPatientHealthRecordSections callable (grant + allowedSections),
 * plus medications subcollection only when the grant includes "medications".
 *
 * Data structure returned (owner — all keys present; non-owner — only granted topics):
 * {
 *   demographics: { firstName, lastName, dateOfBirth, sex, phone, ... },
 *   pmh: [...],
 *   psh: [...],
 *   medications: [...],
 *   allergies: [...],
 *   other: {...},
 *   viaSecureLink?: true,   // only on gated non-owner path
 *   allowedSections?: string[],
 * }
 */

import { db, firebaseApp } from '@healthdesk/shared-services';

const READ_SECTIONS_FUNCTION = 'readPatientHealthRecordSections';

function normalizeMedicationDocs(docs) {
  return docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      name: data.name || data.medicationName || '',
      prescriber: data.prescriber || data.prescribedBy || '',
      reason: data.reason || data.notes || '',
    };
  });
}

async function fetchMedicationsSubcollection(patientId) {
  const medsSnapshot = await db
    .collection('users')
    .doc(patientId)
    .collection('medications')
    .get();
  return normalizeMedicationDocs(medsSnapshot.docs);
}

function normalizeDateOfBirth(raw) {
  if (!raw) return null;
  if (raw.toDate) return raw.toDate().toISOString();
  if (raw instanceof Date) return raw.toISOString();
  if (typeof raw === 'string') return raw;
  return null;
}

function buildOwnerCmrFromDocs(patientId, userData, medications) {
  const healthRecords = userData?.healthRecords || {};
  const dateOfBirth = normalizeDateOfBirth(healthRecords.dateOfBirth);

  let pmhArray = [];
  if (healthRecords.pmh) {
    if (Array.isArray(healthRecords.pmh)) {
      pmhArray = healthRecords.pmh.filter((p) => p && p.condition);
    } else if (typeof healthRecords.pmh === 'object' && healthRecords.pmh.condition) {
      pmhArray = [healthRecords.pmh];
    }
  }

  let pshArray = [];
  if (healthRecords.psh) {
    if (Array.isArray(healthRecords.psh)) {
      pshArray = healthRecords.psh.filter((s) => s && s.procedure);
    } else if (typeof healthRecords.psh === 'object' && healthRecords.psh.procedure) {
      pshArray = [healthRecords.psh];
    }
  }

  let allergiesArray = [];
  if (healthRecords.allergies) {
    if (Array.isArray(healthRecords.allergies)) {
      allergiesArray = healthRecords.allergies.filter((a) => a && a.allergen);
    } else if (typeof healthRecords.allergies === 'object' && healthRecords.allergies.allergen) {
      allergiesArray = [healthRecords.allergies];
    }
  }

  return {
    demographics: {
      firstName: healthRecords.firstName || userData?.name?.split(' ')[0] || '',
      lastName: healthRecords.lastName || userData?.name?.split(' ').slice(1).join(' ') || '',
      dateOfBirth,
      sex: healthRecords.sex || '',
      phone: healthRecords.phone || userData?.phone || '',
      email: healthRecords.email || userData?.email || '',
      address: healthRecords.address || null,
      emergencyContact: healthRecords.emergencyContact || null,
      weight: healthRecords.weight != null ? healthRecords.weight : null,
      weightDate: healthRecords.weightDate || null,
    },
    pmh: pmhArray,
    psh: pshArray,
    medications: Array.isArray(medications) ? medications : [],
    allergies: allergiesArray,
    other:
      healthRecords.other && typeof healthRecords.other === 'object'
        ? healthRecords.other
        : {},
    updatedAt: healthRecords.updatedAt || null,
    patientId,
  };
}

/**
 * Map gated callable response → CMR shape. Ungranted topics are omitted entirely.
 */
export function mapGatedHealthRecordsToCmr(patientId, gated) {
  const allowedSections = Array.isArray(gated?.allowedSections)
    ? gated.allowedSections
    : [];
  const hr = gated?.healthRecords || {};
  const cmr = {
    patientId,
    viaSecureLink: true,
    allowedSections,
  };

  if (Object.prototype.hasOwnProperty.call(hr, 'demographics')) {
    const demo = hr.demographics || {};
    cmr.demographics = {
      firstName: demo.firstName || '',
      lastName: demo.lastName || '',
      dateOfBirth: normalizeDateOfBirth(demo.dateOfBirth),
      sex: demo.sex || '',
      phone: demo.phone || '',
      email: demo.email || '',
      address: demo.address || null,
      emergencyContact: demo.emergencyContact || null,
      weight: demo.weight != null ? demo.weight : null,
      weightDate: demo.weightDate || null,
    };
  }
  if (Object.prototype.hasOwnProperty.call(hr, 'conditions')) {
    const pmh = hr.conditions;
    cmr.pmh = Array.isArray(pmh)
      ? pmh.filter((p) => p && p.condition)
      : [];
  }
  if (Object.prototype.hasOwnProperty.call(hr, 'surgical')) {
    const psh = hr.surgical;
    cmr.psh = Array.isArray(psh)
      ? psh.filter((s) => s && s.procedure)
      : [];
  }
  if (Object.prototype.hasOwnProperty.call(hr, 'allergies')) {
    const allergies = hr.allergies;
    cmr.allergies = Array.isArray(allergies)
      ? allergies.filter((a) => a && a.allergen)
      : [];
  }
  if (Object.prototype.hasOwnProperty.call(hr, 'other')) {
    cmr.other =
      hr.other && typeof hr.other === 'object' && !Array.isArray(hr.other)
        ? hr.other
        : {};
  }

  return cmr;
}

async function fetchPatientCMRViaGrant(patientId, { sessionId } = {}) {
  const callable = firebaseApp.functions().httpsCallable(READ_SECTIONS_FUNCTION);
  const payload = { patientUid: patientId };
  if (sessionId) payload.sessionId = sessionId;
  const { data } = await callable(payload);
  const cmr = mapGatedHealthRecordsToCmr(patientId, data);
  if (Array.isArray(data?.medications)) {
    cmr.medications = data.medications;
  } else if (
    Array.isArray(data?.allowedSections) &&
    data.allowedSections.includes('medications')
  ) {
    try {
      cmr.medications = await fetchMedicationsSubcollection(patientId);
    } catch (err) {
      console.warn('[CMR Read] medications subcollection read failed', err);
      cmr.medications = [];
    }
  }
  return cmr;
}

/**
 * Fetch complete CMR data for a patient
 * @param {string} patientId - The patient's Firebase UID
 * @param {{ sessionId?: string }} [options] - Optional VisitStart / TCI session id for staff-proxy-cmr grants
 * @returns {Promise<Object>} Complete CMR data or null if not found
 */
export async function fetchPatientCMR(patientId, options = {}) {
  if (!patientId) {
    console.warn('[CMR Read] No patient ID provided');
    return null;
  }

  try {
    const callerUid = firebaseApp.auth().currentUser?.uid;
    if (callerUid && callerUid !== patientId) {
      return await fetchPatientCMRViaGrant(patientId, options);
    }

    const userDoc = await db.collection('users').doc(patientId).get();
    if (!userDoc.exists) {
      console.warn('[CMR Read] Patient document not found:', patientId);
      return null;
    }
    const medications = await fetchMedicationsSubcollection(patientId);
    return buildOwnerCmrFromDocs(patientId, userDoc.data(), medications);
  } catch (error) {
    console.error('[CMR Read] Error fetching CMR:', error);
    // Do NOT return an empty CMR shell — callers would treat failure as
    // "patient has no allergies/meds/history". Surface the error instead.
    throw error;
  }
}

/**
 * Fetch specific section of CMR data
 * @param {string} patientId - The patient's Firebase UID
 * @param {string} section - Section name: 'demographics', 'pmh', 'psh', 'medications', 'allergies', 'other'
 * @returns {Promise<Object|Array>} Section data or null
 */
export async function fetchPatientCMRSection(patientId, section) {
  if (!patientId || !section) {
    return null;
  }

  try {
    const callerUid = firebaseApp.auth().currentUser?.uid;
    if (callerUid && callerUid !== patientId) {
      const cmr = await fetchPatientCMRViaGrant(patientId);
      if (section === 'medications') {
        return Object.prototype.hasOwnProperty.call(cmr, 'medications')
          ? cmr.medications
          : null;
      }
      if (!Object.prototype.hasOwnProperty.call(cmr, section)) {
        return null;
      }
      return cmr[section];
    }

    // Special handling for medications (subcollection)
    if (section === 'medications') {
      return fetchMedicationsSubcollection(patientId);
    }

    // All other sections from healthRecords
    const userDoc = await db.collection('users').doc(patientId).get();
    if (!userDoc.exists) return null;

    const userData = userDoc.data();
    const healthRecords = userData?.healthRecords || {};

    switch (section) {
      case 'demographics':
        return {
          firstName: healthRecords.firstName || userData?.name?.split(' ')[0] || '',
          lastName: healthRecords.lastName || userData?.name?.split(' ').slice(1).join(' ') || '',
          dateOfBirth: healthRecords.dateOfBirth || null,
          sex: healthRecords.sex || '',
          phone: healthRecords.phone || userData?.phone || '',
          email: healthRecords.email || userData?.email || '',
          address: healthRecords.address || null,
          emergencyContact: healthRecords.emergencyContact || null,
          weight: healthRecords.weight != null ? healthRecords.weight : null,
          weightDate: healthRecords.weightDate || null,
        };
      case 'pmh':
        return healthRecords.pmh || [];
      case 'psh':
        return healthRecords.psh || [];
      case 'allergies':
        return healthRecords.allergies || [];
      case 'other':
        return healthRecords.other || {};
      default:
        console.warn('[CMR Read] Unknown section:', section);
        return null;
    }
  } catch (error) {
    console.error('[CMR Read] Error fetching CMR section:', error);
    // Distinguish load failure from a legitimately empty section.
    throw error;
  }
}

/**
 * Calculate age from date of birth
 * @param {string|Date} dateOfBirth - Date of birth
 * @returns {number|null} Age in years or null
 */
export function calculateAge(dateOfBirth) {
  if (!dateOfBirth) return null;
  
  const dob = new Date(dateOfBirth);
  if (isNaN(dob.getTime())) return null;
  
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--;
  }
  
  return age;
}

/**
 * Format CMR data for LLM context (used in chart generation prompts)
 * @param {Object} cmrData - Complete CMR data object
 * @returns {string} Formatted string for LLM context
 */
export function formatCMRForLLM(cmrData) {
  if (!cmrData) return '';
  
  const sections = [];
  
  // Demographics
  const demo = cmrData.demographics;
  if (demo?.firstName || demo?.lastName) {
    const age = calculateAge(demo.dateOfBirth);
    const ageStr = age ? `, ${age}yo` : '';
    const sexStr = demo.sex ? ` ${demo.sex}` : '';
    sections.push(`PATIENT: ${demo.firstName} ${demo.lastName}${ageStr}${sexStr}`);
  }
  
  // PMH
  if (cmrData.pmh?.length > 0) {
    const conditions = cmrData.pmh
      .map(c => `${c.condition}${c.status === 'active' ? ' (active)' : c.status === 'resolved' ? ' (resolved)' : ''}`)
      .join(', ');
    sections.push(`PMH: ${conditions}`);
  } else {
    sections.push('PMH: None documented');
  }
  
  // PSH
  if (cmrData.psh?.length > 0) {
    const surgeries = cmrData.psh
      .map(s => `${s.procedure}${s.year ? ` (${s.year})` : ''}`)
      .join(', ');
    sections.push(`PSH: ${surgeries}`);
  } else {
    sections.push('PSH: None documented');
  }
  
  // Medications
  if (cmrData.medications?.length > 0) {
    const meds = cmrData.medications
      .map(m => `${m.name}${m.dosage ? ` ${m.dosage}` : ''}`)
      .join(', ');
    sections.push(`MEDICATIONS: ${meds}`);
  } else {
    sections.push('MEDICATIONS: None documented');
  }
  
  // Allergies
  if (cmrData.allergies?.length > 0) {
    const allergies = cmrData.allergies
      .map(a => `${a.allergen}${a.reaction ? ` - ${a.reaction}` : ''}`)
      .join(', ');
    sections.push(`ALLERGIES: ${allergies}`);
  } else {
    sections.push('ALLERGIES: NKDA');
  }
  
  // Social/Family History
  const other = cmrData.other;
  if (other) {
    if (other.smokingStatus) sections.push(`SMOKING: ${other.smokingStatus}`);
    if (other.alcoholUse) sections.push(`ALCOHOL: ${other.alcoholUse}`);
    if (other.familyHistory) sections.push(`FAMILY HX: ${other.familyHistory}`);
  }

  // Review of Systems (from SecureLink intake extraction)
  const ros = cmrData.ros ?? cmrData.cmrCards?.ros;
  if (Array.isArray(ros) && ros.length > 0) {
    const rosLines = ros
      .map((item) => {
        const system = item.system ?? item.systemName ?? '';
        const value = (item.value ?? item.summary ?? '').toString().trim() || 'No complaints';
        return system ? `${system}: ${value}` : value;
      })
      .filter(Boolean);
    if (rosLines.length > 0) {
      sections.push('REVIEW OF SYSTEMS (ROS):\n' + rosLines.join('\n'));
    }
  }
  
  return sections.join('\n');
}

/**
 * Format CMR data for LLM context with filtering based on card inclusions.
 * "(via SecureLink)" labels appear ONLY when cmrData.viaSecureLink === true
 * (gated non-owner callable path). Ungated/owner data never gets that label.
 *
 * When viaSecureLink, only sections present on the object are formatted
 * (absence = not consented), regardless of inclusions defaults.
 *
 * @param {Object} cmrData - Complete CMR data object
 * @param {Object} inclusions - Object specifying which sections to include
 *   { demographics: true, pmh: true, psh: true, medications: true, allergies: true, other: true }
 * @returns {string} Formatted string for LLM context with only included sections
 */
export function formatCMRForLLMFiltered(cmrData, inclusions = {}) {
  if (!cmrData) return '';

  const gated = cmrData.viaSecureLink === true;
  const has = (key) => Object.prototype.hasOwnProperty.call(cmrData, key);

  // Default all to true if not specified (backwards compatible for non-gated)
  const include = {
    demographics: inclusions.demographics !== false,
    pmh: inclusions.pmh !== false,
    psh: inclusions.psh !== false,
    medications: inclusions.medications !== false,
    allergies: inclusions.allergies !== false,
    other: inclusions.other !== false,
  };

  const tag = gated ? ' (via SecureLink)' : '';
  const sections = [];

  // Demographics
  if (include.demographics && (!gated || has('demographics'))) {
    const demo = cmrData.demographics;
    if (demo?.firstName || demo?.lastName) {
      const age = calculateAge(demo.dateOfBirth);
      const ageStr = age ? `, ${age}yo` : '';
      const sexStr = demo.sex ? ` ${demo.sex}` : '';
      sections.push(`PATIENT DEMOGRAPHICS${tag}: ${demo.firstName} ${demo.lastName}${ageStr}${sexStr}`);
    }
  }

  // PMH
  if (include.pmh && (!gated || has('pmh'))) {
    if (cmrData.pmh?.length > 0) {
      const conditions = cmrData.pmh
        .map(c => `${c.condition}${c.status === 'active' ? ' (active)' : c.status === 'resolved' ? ' (resolved)' : ''}`)
        .join(', ');
      sections.push(`PATIENT PAST MEDICAL HISTORY${tag}: ${conditions}`);
    } else if (!gated) {
      sections.push(`PATIENT PAST MEDICAL HISTORY${tag}: None documented`);
    } else {
      sections.push(`PATIENT PAST MEDICAL HISTORY${tag}: None documented`);
    }
  }

  // PSH
  if (include.psh && (!gated || has('psh'))) {
    if (cmrData.psh?.length > 0) {
      const surgeries = cmrData.psh
        .map(s => `${s.procedure}${s.year ? ` (${s.year})` : ''}`)
        .join(', ');
      sections.push(`PATIENT PAST SURGICAL HISTORY${tag}: ${surgeries}`);
    } else {
      sections.push(`PATIENT PAST SURGICAL HISTORY${tag}: None documented`);
    }
  }

  // Medications - CRITICAL for medication inference
  if (include.medications && (!gated || has('medications'))) {
    if (cmrData.medications?.length > 0) {
      const meds = cmrData.medications
        .map(m => `${m.name}${m.dosage ? ` ${m.dosage}` : ''}${m.frequency ? ` ${m.frequency}` : ''}`)
        .join(', ');
      sections.push(`PATIENT MEDICATIONS${tag}: ${meds}`);
      sections.push('NOTE: Use the medication list above to INFER underlying conditions (e.g., antihypertensives → hypertension)');
    } else {
      sections.push(`PATIENT MEDICATIONS${tag}: None documented`);
    }
  }

  // Allergies
  if (include.allergies && (!gated || has('allergies'))) {
    if (cmrData.allergies?.length > 0) {
      const allergies = cmrData.allergies
        .map(a => `${a.allergen}${a.reaction ? ` - ${a.reaction}` : ''}`)
        .join(', ');
      sections.push(`PATIENT ALLERGIES${tag}: ${allergies}`);
    } else {
      sections.push(`PATIENT ALLERGIES${tag}: NKDA`);
    }
  }

  // Social/Family History
  if (include.other && (!gated || has('other'))) {
    const other = cmrData.other;
    if (other) {
      const otherItems = [];
      if (other.smokingStatus) otherItems.push(`Smoking: ${other.smokingStatus}`);
      if (other.alcoholUse) otherItems.push(`Alcohol: ${other.alcoholUse}`);
      if (other.drugUse) otherItems.push(`Drug use: ${other.drugUse}`);
      if (other.familyHistory) otherItems.push(`Family Hx: ${other.familyHistory}`);
      if (other.socialHistory) otherItems.push(`Social Hx: ${other.socialHistory}`);

      if (otherItems.length > 0) {
        sections.push(`PATIENT SOCIAL/FAMILY HISTORY${tag}: ${otherItems.join('; ')}`);
      }
    }
  }

  // Conversation Results (from SecureLink intake conversation)
  if (cmrData.conversationResults?.findings?.length > 0) {
    const cr = cmrData.conversationResults;
    let convoSection = '\n--- INTAKE CONVERSATION RESULTS ---';
    if (cr.title) convoSection += `\nConversation: ${cr.title}`;
    if (cr.summary) convoSection += `\nSummary: ${cr.summary}`;
    convoSection += '\nFindings:';
    cr.findings.forEach(f => {
      convoSection += `\n  - ${f.label}: ${f.value}`;
    });
    sections.push(convoSection);
  }

  // Review of Systems (from SecureLink intake extraction)
  const includeRos = inclusions.ros !== false;
  const ros = cmrData.ros ?? cmrData.cmrCards?.ros;
  if (includeRos && Array.isArray(ros) && ros.length > 0) {
    const rosLines = ros
      .map((item) => {
        const system = item.system ?? item.systemName ?? '';
        const value = (item.value ?? item.summary ?? '').toString().trim() || 'No complaints';
        return system ? `${system}: ${value}` : value;
      })
      .filter(Boolean);
    if (rosLines.length > 0) {
      sections.push('\n--- REVIEW OF SYSTEMS (ROS) ---\n' + rosLines.join('\n'));
    }
  }

  return sections.join('\n');
}

/**
 * Check if CMR data has meaningful content
 * @param {Object} cmrData - Complete CMR data object
 * @returns {boolean} True if CMR has any meaningful data
 */
export function hasCMRData(cmrData) {
  if (!cmrData) return false;

  return (
    !!(cmrData.demographics?.firstName || cmrData.demographics?.lastName) ||
    (Array.isArray(cmrData.pmh) && cmrData.pmh.length > 0) ||
    (Array.isArray(cmrData.psh) && cmrData.psh.length > 0) ||
    (Array.isArray(cmrData.medications) && cmrData.medications.length > 0) ||
    (Array.isArray(cmrData.allergies) && cmrData.allergies.length > 0) ||
    Object.keys(cmrData.other || {}).length > 0
  );
}

export default {
  fetchPatientCMR,
  fetchPatientCMRSection,
  calculateAge,
  formatCMRForLLM,
  formatCMRForLLMFiltered,
  hasCMRData,
  mapGatedHealthRecordsToCmr,
};

